"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { FileUp, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

type CreatedReportResponse = {
  ok: boolean;
  data?: { report: { id: string } };
  error?: { message: string };
};

type UploadResult = {
  ok: boolean;
  data?: {
    file: { id: string; originalFilename?: string | null };
    upload: {
      mode: "dev-mock" | "oss-sts";
      objectKey: string;
      note?: string;
      bucket?: string;
      endpoint?: string;
      region?: string;
      credentials?: {
        accessKeyId: string;
        accessKeySecret: string;
        securityToken: string;
        expiration: string;
      };
    };
  };
  error?: { message: string };
};

export default function NewReportPage() {
  const router = useRouter();
  const [description, setDescription] = useState("");
  const [workCity, setWorkCity] = useState("");
  const [employerType, setEmployerType] = useState("");
  const [retirementStatus, setRetirementStatus] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  const [uploadClueText, setUploadClueText] = useState("");
  const [uploadIds, setUploadIds] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  async function createReport() {
    setIsSubmitting(true);
    setMessage("");
    const response = await fetch("/api/reports", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: "遗产线索查询报告",
        description,
        uploadIds,
        baseInfo: {
          workCity,
          employerType,
          retirementStatus,
        },
      }),
    });
    const result = (await response.json()) as CreatedReportResponse;
    setIsSubmitting(false);

    if (!result.ok || !result.data?.report.id) {
      setMessage(result.error?.message || "报告草稿创建失败。请确认已登录。");
      return;
    }

    router.push(`/reports/${result.data.report.id}/processing`);
  }

  async function requestUpload(file: File) {
    setUploadMessage("");
    setIsUploading(true);
    try {
      const response = await fetch("/api/upload/sts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filename: file.name,
          mimeType: file.type,
          fileSize: file.size,
          filePurpose: "report-clue",
          clientExtractedText: uploadClueText,
        }),
      });
      const result = (await response.json()) as UploadResult;
      if (!result.ok || !result.data) {
        setUploadMessage(result.error?.message || "上传授权失败。");
        return;
      }

      const { file: uploadFile, upload } = result.data;
      if (upload.mode === "oss-sts") {
        if (!upload.credentials || !upload.bucket || !upload.endpoint || !upload.region) {
          throw new Error("上传授权信息不完整。");
        }
        setUploadMessage("正在安全上传文件...");
        const { default: OSS } = await import("ali-oss");
        const client = new OSS({
          region: upload.region,
          endpoint: upload.endpoint,
          bucket: upload.bucket,
          accessKeyId: upload.credentials.accessKeyId,
          accessKeySecret: upload.credentials.accessKeySecret,
          stsToken: upload.credentials.securityToken,
          authorizationV4: true,
          secure: true,
        });
        const uploaded = await client.put(upload.objectKey, file, {
          mime: file.type,
          headers: { "x-oss-forbid-overwrite": "true" },
        });
        const responseHeaders = (uploaded.res?.headers ?? {}) as Record<string, unknown>;
        const etag = String(responseHeaders.etag ?? "").replace(/^\"|\"$/g, "");
        const completeResponse = await fetch(`/api/uploads/${uploadFile.id}/complete`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ objectKey: upload.objectKey, etag: etag || undefined }),
        });
        const completed = (await completeResponse.json()) as UploadResult;
        if (!completed.ok) {
          throw new Error(completed.error?.message || "上传校验失败。");
        }
      }

      setUploadIds((current) => [...new Set([...current, uploadFile.id])]);
      setUploadMessage(`文件“${file.name}”已安全上传。`);
    } catch (error) {
      setUploadMessage(error instanceof Error ? error.message : "文件上传失败，请重试。");
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <main className="min-h-screen bg-background">
      <div className="container py-10">
        <div className="mb-8 max-w-3xl">
          <p className="mb-3 text-sm font-medium text-primary">步骤 1/2</p>
          <h1 className="text-3xl font-semibold text-slate-950">
            告诉我们逝者的基本情况
          </h1>
          <p className="mt-4 text-base leading-7 text-muted-foreground">
            提交后会创建报告任务。请优先填写低敏线索；如需上传资料，建议使用已删除真实姓名的悼词、履历或手机桌面截图，不要上传身份证、退休证、银行卡号、房产证、保险单等高敏材料。
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_0.8fr]">
          <Card className="shadow-subtle">
            <CardHeader>
              <CardTitle>文字描述与基础信息</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <textarea
                className="min-h-40 w-full rounded-md border border-input bg-white p-3 text-sm leading-6 outline-none focus:ring-2 focus:ring-ring"
                placeholder="例如：逝者是香港保险行业高管，曾在国有金融企业工作；手机桌面看到工行、农行、建行、富途牛牛、同花顺等App。"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
              <div className="grid gap-4 md:grid-cols-3">
                <input
                  className="h-11 rounded-md border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                  placeholder="主要工作城市"
                  value={workCity}
                  onChange={(event) => setWorkCity(event.target.value)}
                />
                <input
                  className="h-11 rounded-md border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                  placeholder="单位性质"
                  value={employerType}
                  onChange={(event) => setEmployerType(event.target.value)}
                />
                <input
                  className="h-11 rounded-md border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                  placeholder="退休/在职情况"
                  value={retirementStatus}
                  onChange={(event) => setRetirementStatus(event.target.value)}
                />
              </div>
              <Button onClick={createReport} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    创建中...
                  </>
                ) : (
                  "创建报告草稿"
                )}
              </Button>
              {message ? (
                <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
                  {message}
                </p>
              ) : null}
            </CardContent>
          </Card>

          <Card className="shadow-subtle">
            <CardHeader>
              <CardTitle>低敏文件线索</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <label className="block space-y-2">
                <span className="text-sm font-medium">截图/文件中可见文字</span>
                <textarea
                  className="min-h-24 w-full rounded-md border border-input bg-white p-3 text-sm leading-6 outline-none focus:ring-2 focus:ring-ring"
                  placeholder="例如：手机桌面可见工行、农行、建行、富途牛牛、同花顺、友邦等App。可删除真实姓名和账号信息。"
                  value={uploadClueText}
                  onChange={(event) => setUploadClueText(event.target.value)}
                />
                <span className="text-xs leading-5 text-muted-foreground">
                  当前测试环境会优先使用这里填写的可见文字辅助推理；生产接入 OCR 后将自动识别图片和文档内容。
                </span>
              </label>
              <label className="flex cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-primary/30 bg-white p-8 text-center hover:bg-accent">
                {isUploading ? (
                  <Loader2 className="mb-3 size-7 animate-spin text-primary" />
                ) : (
                  <FileUp className="mb-3 size-7 text-primary" />
                )}
                <span className="font-medium">
                  {isUploading ? "正在上传..." : "选择 PDF、Word 或图片"}
                </span>
                <span className="mt-2 text-sm leading-6 text-muted-foreground">
                  建议上传逝者悼词（可删除真实姓名）、履历、手机桌面截图等低敏线索。
                  不建议上传身份证、退休证、银行卡号、房产证、保险单等高敏材料。
                </span>
                <input
                  className="hidden"
                  type="file"
                  disabled={isUploading}
                  accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/webp,.doc,.docx"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                      void requestUpload(file);
                    }
                  }}
                />
              </label>
              {uploadMessage ? (
                <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
                  {uploadMessage}
                </p>
              ) : null}
              {uploadIds.length ? (
                <p className="rounded-md bg-accent p-3 text-sm text-primary">
                  已关联 {uploadIds.length} 个上传线索到本次报告。
                </p>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>
    </main>
  );
}
